/**
 * `/pair` — approve a sign-in on a machine that has no browser.
 *
 * A CLI on an EC2 box, in a container, or over SSH cannot complete the loopback
 * OAuth flow: Google would redirect the human's OWN browser to the human's OWN
 * localhost, where nothing is listening. So the machine shows a code and the
 * human types it here, in a browser they already have, already signed in.
 *
 * ── This page is the phishing surface, and it says so ────────────────────
 * Every device-code flow shares one weakness: an attacker starts a flow on
 * THEIR machine, sends the victim the code, and the victim authorises the
 * attacker's session with their own identity. There is no protocol fix — the
 * defence is that the person approving understands what they are approving.
 *
 * Hence the warning, worded as a question about provenance rather than a
 * generic "be careful": someone who did not just run the installer has no
 * reason to be here at all, and telling them exactly that is the only thing
 * that reliably stops the attack.
 */

import { route } from '../../router.js'
import { esc } from '../../shared/rendering.js'
import { apiFetch, apiMutate } from '../../api/client.js'
import { oauthRedirectUri } from '../../auth/redirectUri.js'
import {
  createPkcePair,
  PAIR_STATE_KEY,
  PKCE_KEYS,
  randomUrlSafe,
  stashVerifier,
  takeVerifier,
} from '../../auth/pkce.js'

/** The API accepts any case; normalising here keeps the field tidy as it is typed. */
function normalise(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '')
}

/** Re-insert the grouping hyphen so what is typed matches what is displayed. */
function formatUserCode(raw: string): string {
  const clean = normalise(raw).slice(0, 8)
  return clean.length > 4 ? `${clean.slice(0, 4)}-${clean.slice(4)}` : clean
}

function render(): void {
  const app = document.getElementById('app')
  if (!app) return

  document.title = 'Authorise a device — Grizzly'
  app.innerHTML = `
    <div class="pair-view">
      <h1>Authorise a device</h1>
      <p class="pair-intro">
        Enter the code shown by the machine you are signing in.
      </p>

      <form id="pair-form" autocomplete="off">
        <label for="pair-code" class="pair-label">Device code</label>
        <input id="pair-code" name="code" class="pair-input"
               inputmode="latin" autocapitalize="characters" spellcheck="false"
               placeholder="XXXX-XXXX" maxlength="9" aria-describedby="pair-warning" />
        <button type="submit" id="pair-submit" class="btn-primary">Authorise</button>
      </form>

      <p id="pair-status" class="pair-status" role="status" aria-live="polite"></p>

      <div id="pair-warning" class="pair-warning">
        <strong>Only continue if you started this yourself.</strong>
        Approving grants that machine access to Grizzly as you. If somebody
        sent you this code — in a message, an email, or a call — close this
        page. There is no legitimate reason to be given a code by anyone else.
      </div>
    </div>
  `

  const form = document.getElementById('pair-form') as HTMLFormElement | null
  const input = document.getElementById('pair-code') as HTMLInputElement | null
  const status = document.getElementById('pair-status')
  const submit = document.getElementById('pair-submit') as HTMLButtonElement | null
  if (!form || !input || !status || !submit) return

  input.addEventListener('input', () => {
    const start = input.selectionStart ?? 0
    const before = input.value.length
    input.value = formatUserCode(input.value)
    // Keep the caret where the typist expects it after the hyphen is inserted.
    const shift = input.value.length - before
    input.setSelectionRange(start + shift, start + shift)
  })

  form.addEventListener('submit', (event) => {
    event.preventDefault()
    const code = normalise(input.value)
    if (code.length !== 8) {
      status.textContent = 'That code is not complete.'
      status.className = 'pair-status err'
      return
    }

    submit.disabled = true
    status.textContent = 'Authorising…'
    status.className = 'pair-status'

    // The approval needs a fresh authorization code so the server can mint a
    // REFRESH token for the device. A refresh token must never pass through
    // this page — that is what would turn an XSS into indefinite access — so
    // the browser only carries the short-lived authorization code, and the
    // exchange happens server-side.
    void startApproval(code, status, submit)
  })

  input.focus()
}

/**
 * Hand the typed code to the server together with proof of who is approving.
 *
 * Redirects through Google so the server receives an authorization code it can
 * exchange with offline access. `state` carries the device code back, because
 * the redirect loses everything else.
 */
async function startApproval(
  userCode: string,
  status: HTMLElement,
  submit: HTMLButtonElement,
): Promise<void> {
  try {
    const cfg = await apiFetch<{
      clientId: string
      authorizeUrl: string
      workspaceDomain: string
    }>('/api/auth/config')

    // Shared with sign-in. These two once differed by a trailing slash, which
    // Google rejects as a different URI entirely — see auth/redirectUri.ts.
    const redirectUri = oauthRedirectUri()

    // PKCE, exactly as sign-in does it. The API refuses an exchange with no
    // code_verifier — failing closed, correctly — and this flow previously did
    // not know the control existed, because the only implementation lived
    // inside the Google provider's private scope. See auth/pkce.ts.
    const { verifier, challenge } = await createPkcePair()
    stashVerifier(PKCE_KEYS.pair, verifier)

    // CSRF nonce, as sign-in has always had. Without it `state` was
    // `pair:<userCode>` with no random component, so a callback forged by
    // anyone who knew a user code was indistinguishable from this browser's
    // own. It is compared on return and bound to the code below.
    const nonce = randomUrlSafe(16)
    sessionStorage.setItem(PAIR_STATE_KEY, `${nonce}:${userCode}`)

    const params = new URLSearchParams({
      client_id: cfg.clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      // Offline access is the point: the device needs a renewable credential
      // or it re-authenticates every hour.
      access_type: 'offline',
      prompt: 'consent',
      hd: cfg.workspaceDomain,
      state: `pair:${nonce}:${userCode}`,
      code_challenge: challenge,
      code_challenge_method: 'S256',
    })
    window.location.assign(`${cfg.authorizeUrl}?${params.toString()}`)
  } catch (error) {
    status.textContent = `Could not start authorisation: ${esc(String(error))}`
    status.className = 'pair-status err'
    submit.disabled = false
  }
}

/**
 * Finish an approval that came back from Google.
 *
 * Called by the app's OAuth landing path when `state` names a pairing rather
 * than an ordinary sign-in.
 */
export async function completePairing(rawState: string, code: string): Promise<void> {
  const app = document.getElementById('app')
  if (!app) return

  document.title = 'Authorise a device — Grizzly'
  app.innerHTML = `<div class="pair-view"><h1>Authorise a device</h1>
    <p id="pair-status" class="pair-status" role="status" aria-live="polite">Authorising…</p></div>`
  const status = document.getElementById('pair-status')

  // Fail closed, exactly as the sign-in callback does. A state this browser did
  // not mint means the round trip did not start here, which is the case CSRF
  // protection exists for. Taken and forgotten so it cannot be replayed.
  const expected = sessionStorage.getItem(PAIR_STATE_KEY)
  sessionStorage.removeItem(PAIR_STATE_KEY)
  const nonceAndCode = rawState.includes(':') ? rawState : ''
  if (!expected || nonceAndCode !== expected) {
    if (status) {
      status.textContent =
        'This approval did not start in this browser, so it was not completed. ' +
        'Begin again on the machine you are signing in.'
      status.className = 'pair-status err'
    }
    return
  }
  const userCode = expected.slice(expected.indexOf(':') + 1)

  try {
    // Single-use: taken and forgotten in one step, so a replayed callback
    // cannot exchange the same code twice.
    const verifier = takeVerifier(PKCE_KEYS.pair)
    if (!verifier) {
      throw new Error('this browser did not start the pairing — begin again on the machine')
    }

    await apiMutate('/api/auth/device/approve', {
      method: 'POST',
      body: {
        user_code: userCode,
        code,
        // Must be byte-identical to the URI the code was requested with.
        redirect_uri: oauthRedirectUri(),
        code_verifier: verifier,
      },
    })
    if (status) {
      status.textContent =
        'Approved. Return to the machine you were signing in — it will continue on its own.'
      status.className = 'pair-status ok'
    }
  } catch (error) {
    if (status) {
      // One message for unknown, used and expired: the server does not
      // distinguish them, so that someone cannot probe for live codes.
      status.textContent = `That code could not be approved. It may have expired — start again on the machine. (${esc(String(error))})`
      status.className = 'pair-status err'
    }
  }
}

route('/pair', () => {
  render()
})
